import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import orderBy from 'lodash/orderBy';

import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';

import {
  SortingState,
  IntegratedSorting,
  IntegratedFiltering,
  IntegratedPaging,
  EditingState,
  FilteringState,
  PagingState,
} from '@devexpress/dx-react-grid';
import { TableEditColumn } from '@devexpress/dx-react-grid-material-ui';

import { showLoading, hideLoading } from '../../../../store/app';
import { openConfirmDialog } from '../../../../store/dialogs';
import { toastr } from '../../../../components/CustomToast';
import BaseTable from '../../../../components/Table/BaseTable';
import HeaderEditCell from '../../../../components/Table/cells/HeaderEditCell';
import EditCell from '../../../../components/Table/cells/EditCell';
import { MembershipRoleTypeProvider } from '../../../../components/Table/providers';
import WebAPIClient, {
  errorResponseToastr,
} from '../../../../api/_energytracer';
import PortfolioUserDialog from '../../../../components/Dialogs/energytracer/PortfolioUserDialog';

export default function PortfolioUsers() {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [open, setOpen] = useState(false);

  const [tableColumnExtensions] = useState([
    { columnName: 'active', align: 'center' },
  ]);
  const [integratedFilteringColumnExtensions] = useState([
    {
      columnName: 'portfolio_name',
      predicate: (value, filter) => {
        return value.toLowerCase().includes(filter.value.toLowerCase());
      },
    },
  ]);
  const [integratedSortingColumnExtensions] = useState([
    {
      columnName: 'portfolio_name',
      compare: (a, b) => {
        return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
      },
    },
  ]);
  const [columns] = useState([
    { title: 'Portfolio', name: 'portfolio_name', width: 0.25 },
    { title: 'First Name', name: 'first_name', width: 0.125 },
    { title: 'Last Name', name: 'last_name', width: 0.125 },
    { title: 'Email', name: 'email', width: 0.35 },
    { title: 'Role', name: 'role', width: 0.15 },
  ]);

  const fetchData = useCallback(async () => {
    dispatch(showLoading());

    try {
      const users = await new WebAPIClient().GET('/profile/users');
      const portfolios = await new WebAPIClient().GET('/portfolios/users');

      let user_lookup = {};
      users.forEach((user) => (user_lookup[user.id] = user));

      let table_rows = [];
      portfolios.forEach((portfolio) => {
        portfolio.users.forEach((portfolio_user) => {
          let user = user_lookup[portfolio_user.user_id];
          table_rows.push({
            portfolio_id: portfolio.id,
            portfolio_name: portfolio.name,
            user_id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            role: portfolio_user.role,
          });
        });
      });
      setUsers(
        orderBy(users, ['email']).map((user) => {
          return { ...user, name: user.first_name + ' ' + user.last_name };
        })
      );
      setPortfolios(orderBy(portfolios, 'name'));
      setTableRows(table_rows);
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
    dispatch(hideLoading());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const handleDeleteClick = (membership) => {
    const message = <p>Are you sure you want to delete this membership?</p>;
    dispatch(
      openConfirmDialog({
        title: 'Delete Membership',
        message: message,
        onConfirm: () => deleteMembership(membership),
      })
    );
  };

  const deleteMembership = async (membership) => {
    dispatch(showLoading());
    try {
      await new WebAPIClient().DELETE(
        `portfolios/${membership.portfolio_id}/users/${membership.user_id}`
      );
      setTableRows((prev) =>
        prev.filter(
          (data) =>
            !(
              data.user_id === membership.user_id &&
              data.portfolio_id === membership.portfolio_id
            )
        )
      );
      dispatch(hideLoading());
      toastr.success({ title: 'Membership deleted' });
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  };

  const handleEditClick = (membership, value) => {
    const message = (
      <div>
        <p>Are you sure you want to update this membership?</p>
      </div>
    );
    dispatch(
      openConfirmDialog({
        title: 'Update Membership',
        message: message,
        onConfirm: () => editMembership(membership, value),
      })
    );
  };

  const editMembership = async (membership, value) => {
    dispatch(showLoading());
    try {
      await new WebAPIClient().POST(
        `portfolios/${membership.portfolio_id}/users/${membership.user_id}`,
        { role: value }
      );
      setTableRows((prev) =>
        prev.map((data) => {
          if (
            data.user_id === membership.user_id &&
            data.portfolio_id === membership.portfolio_id
          )
            return { ...data, role: value };
          return data;
        })
      );
      dispatch(hideLoading());
      toastr.success({ title: 'Membership Updated' });
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  };

  return (
    <Grid
      container
      direction='row'
      justifyContent='center'
      alignItems='stretch'
      spacing={1}
      sx={{ maxWidth: '100%', mt: 1 }}
    >
      <Grid
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          maxWidth: '100vw',
          px: 1,
        }}
      >
        <Card raised sx={{ maxWidth: 1000, width: '100%', px: 1 }}>
          <BaseTable
            minWidth={1000}
            rows={tableRows}
            columns={columns}
            tableColumnExtensions={tableColumnExtensions}
            editColumn={
              <TableEditColumn
                width={42}
                headerCellComponent={(props) => {
                  return (
                    <HeaderEditCell
                      disabled={false}
                      tooltipText='Create Membership'
                      handleClick={() => setOpen(true)}
                      {...props}
                    />
                  );
                }}
                cellComponent={(props) => (
                  <EditCell
                    disabled={false}
                    tooltipText='Delete Membership'
                    icon={['fal', 'trash']}
                    // eslint-disable-next-line
                    handleClick={() => handleDeleteClick(props.row)}
                    {...props}
                  />
                )}
              />
            }
          >
            <MembershipRoleTypeProvider
              for={['role']}
              appId={'energytracer'}
              handleClick={handleEditClick}
            />
            <EditingState />
            <FilteringState />
            <SortingState
              defaultSorting={[
                { columnName: 'portfolio_name', direction: 'asc' },
              ]}
            />
            <PagingState defaultCurrentPage={0} pageSize={10} />

            <IntegratedFiltering
              columnExtensions={integratedFilteringColumnExtensions}
            />
            <IntegratedSorting
              columnExtensions={integratedSortingColumnExtensions}
            />
            <IntegratedPaging />
          </BaseTable>
        </Card>
        <PortfolioUserDialog
          users={users}
          portfolios={portfolios}
          setTableRows={setTableRows}
          open={open}
          setOpen={setOpen}
        />
      </Grid>
    </Grid>
  );
}
